import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/clinic/Navbar";
import Footer from "@/components/clinic/Footer";
import { applySeo } from "@/lib/seo";
import { clinicNAP } from "@/lib/clinic";
import { useToast } from "@/hooks/use-toast";

interface GbpPost {
  id: number;
  label: string;
  angle: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  hashtags: string[];
}

const BOOKING_URL = clinicNAP.bookingUrl;

const GbpPosts = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState<GbpPost[] | null>(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    applySeo({
      title: "Google Business Posts — Dr Vahe Clinic Admin",
      description: "Draft evergreen Google Business Profile posts for the current blepharoplasty promotion.",
      path: "/admin/gbp-posts",
      ogType: "article",
    });
    // Discourage indexing of admin tools
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => {
      robots?.setAttribute("content", "index, follow");
    };
  }, []);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("generate-gbp-posts", {
        body: {
          bookingUrl: BOOKING_URL,
          notes,
          clinicName: clinicNAP.name,
          clinicAddress: `${clinicNAP.address.street}, ${clinicNAP.address.locality}, ${clinicNAP.address.region} ${clinicNAP.address.postalCode}`,
        },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      if (!Array.isArray(data?.posts)) throw new Error("Unexpected response from generator.");
      setPosts(data.posts);
      toast({ title: "Drafts ready", description: "6 promotional posts generated." });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Generation failed";
      setError(msg);
      toast({ title: "Could not generate", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const copyPost = async (p: GbpPost) => {
    const tags = p.hashtags?.length ? `\n\n${p.hashtags.map((h) => `#${h}`).join(" ")}` : "";
    const text = `${p.body}${tags}\n\n${p.ctaLabel}: ${p.ctaUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: `${p.label} post copied to clipboard.` });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the text manually instead.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <article className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
          >
            ← Back to clinic
          </Link>

          <header className="mt-8 mb-12">
            <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
              Admin · Local SEO
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-4 text-foreground">
              Google Business <span className="italic">Posts</span>
            </h1>
            <p className="mt-4 text-muted-foreground font-light max-w-2xl leading-relaxed">
              Generate six reusable Google Business Profile drafts for the current blepharoplasty
              promotion, each with a distinct angle and clear call to action. Review, copy, and
              paste into your Google Business dashboard.
            </p>
          </header>

          <section className="bg-linen-deep border border-border rounded-2xl p-6 md:p-8 mb-10">
            <label className="block text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
              Optional context for the AI
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Highlight recovery, consultation, aftercare or payment options."
              className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-light leading-relaxed focus:outline-none focus:ring-1 focus:ring-accent/40"
            />
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground/80 font-light">
                Booking URL: <span className="text-foreground/80">{BOOKING_URL}</span>
              </p>
              <button
                type="button"
                onClick={generate}
                disabled={loading}
                className="px-7 py-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.3em] rounded-full hover:bg-accent transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Drafting…" : posts ? "Regenerate all" : "Generate 6 posts"}
              </button>
            </div>
            {error && (
              <p className="mt-4 text-sm text-destructive font-light" role="alert">
                {error}
              </p>
            )}
          </section>

          {posts && (
            <section aria-label="Generated posts" className="space-y-6">
              {posts.map((p) => (
                <article
                  key={p.id}
                  className="bg-background border border-border rounded-2xl p-6 md:p-7 shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-accent font-medium">
                        {p.label}
                      </p>
                      <p className="text-xs text-muted-foreground font-light mt-1">
                        Angle · {p.angle}
                      </p>
                    </div>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground/70">
                      {p.body.length} chars
                    </span>
                  </div>

                  <p className="text-foreground font-light leading-relaxed whitespace-pre-wrap">
                    {p.body}
                  </p>

                  {p.hashtags?.length > 0 && (
                    <p className="mt-3 text-xs text-muted-foreground font-light">
                      {p.hashtags.map((h) => `#${h}`).join("  ")}
                    </p>
                  )}

                  <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                    <div className="text-xs font-light">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mr-2">
                        CTA
                      </span>
                      <span className="text-foreground/90">{p.ctaLabel}</span>
                      <span className="text-muted-foreground/70 mx-2">→</span>
                      <a
                        href={p.ctaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:text-accent/70 underline underline-offset-2"
                      >
                        {p.ctaUrl}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyPost(p)}
                      className="px-5 py-2 border border-border rounded-full text-[10px] uppercase tracking-[0.3em] hover:border-accent hover:text-accent transition-colors"
                    >
                      Copy post
                    </button>
                  </div>
                </article>
              ))}
            </section>
          )}

          {!posts && !loading && (
            <p className="text-sm text-muted-foreground font-light">
              No drafts yet. Click <em>Generate 6 posts</em> to draft the week.
            </p>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default GbpPosts;
